import { useRef, useState } from 'react';
import { api } from '@aeon-karaoke/cloud-convex';
import type { Id } from '@aeon-karaoke/cloud-convex/_generated/dataModel';
import { convex } from '@/lib/convex';
import { toResult, type ConvexCallError } from '@/lib/result-adapter';
import type { Result } from '@aeon-karaoke/shared/generic/impl/util/result';

/** What createDraft, createAeonTrack and replaceSource hand back. */
export interface UploadIntent {
  trackId: Id<'tracks'>;
  uploadUrl: string;
}

/** A stalled PUT settles rather than pinning the form open. */
const PUT_TIMEOUT_MS = 15 * 60 * 1000;

/** PUT the bytes straight to R2 with progress, since fetch cannot report it. */
function putFile(
  xhr: XMLHttpRequest,
  url: string,
  file: File,
  onProgress: (fraction: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    xhr.open('PUT', url);
    xhr.timeout = PUT_TIMEOUT_MS;
    if (file.type) xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));
    xhr.send(file);
  });
}

/** The second and third steps of the upload contract: PUT to the presigned
 * URL the server issued, then ask the server to confirm the object landed.
 * The caller creates the intent first, so the server owns the key and checks
 * the plan limit before any bytes move. */
export function useTrackUpload() {
  const [progress, setProgress] = useState<number | null>(null);
  const active = useRef<XMLHttpRequest | null>(null);

  async function upload(file: File, intent: UploadIntent): Promise<Result<null, ConvexCallError>> {
    setProgress(0);
    const xhr = new XMLHttpRequest();
    active.current = xhr;
    const put = await toResult(() => putFile(xhr, intent.uploadUrl, file, setProgress));
    active.current = null;
    if (!put.ok) {
      setProgress(null);
      return put;
    }
    const confirmed = await toResult(() => convex.action(api.tracks.completeUpload, { trackId: intent.trackId }));
    setProgress(null);
    return confirmed;
  }

  /** Stop a PUT in flight, so discarding the draft cannot race an object into
   * R2 after its row is gone. */
  function abort() {
    active.current?.abort();
  }

  return { upload, abort, progress, uploading: progress !== null };
}
